import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect } from 'react';
import { 
  fetchUserDashboard, 
  fetchUserDashboardWithParams,
  setCurrentPage, 
  setPageSize, 
  setDateTime,
  clearDateTime,
  setType,
  setStatus,
  clearError, 
  clearMessage,
  clearDashboardData
} from '../features/userdashboard/userdashboard.slice';
import type { RootState, AppDispatch } from  '../store';
import { UserDashboardParams } from '../api/userdashboard/index';

const sanitizeParams = (params: Partial<UserDashboardParams>): Partial<UserDashboardParams> => {
  return {
    ...(params.PageNumber && { PageNumber: Math.max(1, Math.floor(Number(params.PageNumber))) }),
    ...(params.PageSize && { PageSize: Math.max(1, Math.floor(Number(params.PageSize))) }),
    ...(params.type !== undefined && params.type !== null && params.type !== 0 && { type: Math.floor(Number(params.type)) }),
    ...(params.status !== undefined && params.status !== null && params.status !== 0 && { status: Math.floor(Number(params.status)) }),
  };
};

export const useUserDashboard = (pageSize= 10) => {
  const dispatch = useDispatch<AppDispatch>();
  
  const {
    dashboardData,
    loading,
    error,
    message,
    pagination,
    filters
  } = useSelector((state: RootState) => state.userDashboard);

  const fetchDashboardData = useCallback(() => {
    const requestParams: UserDashboardParams = {
      PageNumber: pagination.currentPage,
      PageSize: pageSize,
      ...(filters.dateTime && { DateTime: filters.dateTime }),
      ...(filters.type && { type: filters.type }),
      ...(filters.status && { status: filters.status }),
    };
    
    const sanitizedParams = sanitizeParams(requestParams);
    const finalParams: UserDashboardParams = {
      PageNumber: sanitizedParams.PageNumber || 1,
      PageSize: pageSize,
      ...(requestParams.DateTime && { DateTime: requestParams.DateTime }),
      ...(sanitizedParams.type && { type: sanitizedParams.type }),
      ...(sanitizedParams.status && { status: sanitizedParams.status }),
    };
    
    dispatch(fetchUserDashboard(finalParams));
  }, [dispatch, pagination.currentPage, pageSize, filters.dateTime, filters.type, filters.status]);

  // Fetch dashboard data with custom parameters
  const fetchDashboardWithCustomParams = useCallback((params: Record<string, any>) => {
    return dispatch(fetchUserDashboardWithParams(params));
  }, [dispatch]);

  // Set current page
  const handleSetCurrentPage = useCallback((page: number) => {
    dispatch(setCurrentPage(page));
  }, [dispatch]);

  // Set page size
  const handleSetPageSize = useCallback((size: number) => {
    dispatch(setPageSize(size));
  }, [dispatch]);

  // Set date time filter
  const handleSetDateTime = useCallback((dateTime: string) => {
    dispatch(setDateTime(dateTime));
  }, [dispatch]);

  // Clear date time filter
  const handleClearDateTime = useCallback(() => {
    dispatch(clearDateTime());
  }, [dispatch]);

  // Set type (stage) filter
  const handleSetType = useCallback((type?: number) => {
    dispatch(setType(type));
  }, [dispatch]);

  // Set status filter
  const handleSetStatus = useCallback((status?: number) => {
    dispatch(setStatus(status));
  }, [dispatch]);

  // Clear error
  const handleClearError = useCallback(() => {
    dispatch(clearError());
  }, [dispatch]);

  // Clear message
  const handleClearMessage = useCallback(() => {
    dispatch(clearMessage());
  }, [dispatch]);

  // Clear all dashboard data
  const handleClearDashboardData = useCallback(() => {
    dispatch(clearDashboardData());
  }, [dispatch]);

  // Auto-fetch data when pagination or filters change
  const autoFetch = useCallback((enabled: boolean = true) => {
    if (enabled) {
      fetchDashboardData();
    }
  }, [fetchDashboardData]);

  // Refresh data (useful for pull-to-refresh or manual refresh)
  const refreshData = useCallback(() => {
    return fetchDashboardData();
  }, [fetchDashboardData]);

  // Go to next page
  const goToNextPage = useCallback(() => {
    if (pagination.hasNextPage) {
      handleSetCurrentPage(pagination.currentPage + 1);
    }
  }, [pagination.hasNextPage, pagination.currentPage, handleSetCurrentPage]);

  // Go to previous page
  const goToPreviousPage = useCallback(() => {
    if (pagination.hasPreviousPage) {
      handleSetCurrentPage(pagination.currentPage - 1);
    }
  }, [pagination.hasPreviousPage, pagination.currentPage, handleSetCurrentPage]);

  // Go to first page
  const goToFirstPage = useCallback(() => {
    handleSetCurrentPage(1);
  }, [handleSetCurrentPage]);

  // Go to last page
  const goToLastPage = useCallback(() => {
    if (pagination.totalPages > 0) {
      handleSetCurrentPage(pagination.totalPages);
    }
  }, [pagination.totalPages, handleSetCurrentPage]);

  // Set date range (helper for common date filtering)
  const setDateRange = useCallback((startDate: string, endDate?: string) => {
    const isoDateTime = new Date(startDate + 'T10:44:25+07:00').toISOString();
    handleSetDateTime(isoDateTime);
  }, [handleSetDateTime]);

  // Reset to default state
  const resetToDefaults = useCallback(() => {
    handleSetCurrentPage(1);
    handleSetPageSize(5);
    handleSetDateTime(new Date().toISOString());
    handleClearError();
    handleClearMessage();
  }, [handleSetCurrentPage, handleSetPageSize, handleSetDateTime, handleClearError, handleClearMessage]);

  return {
    dashboardData,
    loading,
    error,
    message,
    pagination,
    filters,
    
    fetchDashboardData,
    fetchDashboardWithCustomParams,
    setCurrentPage: handleSetCurrentPage,
    setPageSize: handleSetPageSize,
    setDateTime: handleSetDateTime,
    clearDateTime: handleClearDateTime,
    setType: handleSetType,
    setStatus: handleSetStatus,
    clearError: handleClearError,
    clearMessage: handleClearMessage,
    clearDashboardData: handleClearDashboardData,
    
    autoFetch,
    refreshData,
    goToNextPage,
    goToPreviousPage,
    goToFirstPage,
    goToLastPage,
    setDateRange,
    resetToDefaults,
    
    hasData: dashboardData?.response && dashboardData.response.length > 0,
    isEmpty: !dashboardData?.response || dashboardData.response.length === 0,
    isFirstPage: pagination.currentPage === 1,
    isLastPage: pagination.currentPage === pagination.totalPages,
    totalItems: pagination.totalCount,
    currentPageItems: dashboardData?.response?.length || 0,
  };
};